import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from "@angular/core"
import { CommonModule } from "@angular/common"
import { MatButtonModule } from "@angular/material/button"
import { MatIconModule } from "@angular/material/icon"
import { MatTooltipModule } from "@angular/material/tooltip"
import { MatDialog, MatDialogRef } from "@angular/material/dialog"
import { isAiredDelayed, StationSchedule } from "../../services/station-schedule.service"
import { LockoutService } from "../../services/lockout.service"
import { getCustomerDisplay, OrderCustomerDisplay } from "../../utils/order-display-amount"
import { ScheduleDetailDialogComponent } from "./schedule-detail-dialog.component"
import { scheduleSpotCost } from "../../utils/schedule-spot-cost"

@Component({
  selector: "app-station-schedule-list",
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule],
  templateUrl: "./station-schedule-list.component.html",
  styleUrl: "./station-schedule-list.component.css",
})
export class StationScheduleListComponent implements OnChanges {
  Math = Math
  private readonly dialog = inject(MatDialog)
  private detailDialogRef: MatDialogRef<ScheduleDetailDialogComponent> | null = null

  @Input() schedules: StationSchedule[] = []
  @Input() loading = false
  @Input() emptyMessage = "No schedules yet"
  @Input() showPagination = false
  @Input() total = 0
  @Input() limit = 25
  @Input() offset = 0
  @Input() showCustomerInfo = false
  @Output() pageChange = new EventEmitter<number>()

  private lockoutDatesByStation = new Map<number, Set<string>>()

  constructor(private lockoutSvc: LockoutService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["schedules"]) {
      this.cacheLockoutsForSchedules(this.schedules)
    }
  }

  viewDetails(row: StationSchedule): void {
    if (this.detailDialogRef) {
      return
    }

    this.detailDialogRef = this.dialog.open(ScheduleDetailDialogComponent, {
      width: "min(900px, 92vw)",
      maxWidth: "92vw",
      maxHeight: "90vh",
      panelClass: "app-schedule-detail-dialog",
      autoFocus: false,
      restoreFocus: true,
      disableClose: true,
      data: {
        scheduleId: row.id,
        customer_firstname: row.customer_firstname,
        customer_lastname: row.customer_lastname,
        customer_accounttype: row.customer_accounttype,
        customer_businessname: row.customer_businessname,
      },
    })

    this.detailDialogRef.backdropClick().subscribe(() => {
      this.detailDialogRef?.componentInstance.close()
    })

    this.detailDialogRef.afterClosed().subscribe(() => {
      this.detailDialogRef = null
    })
  }

  isScheduleLocked(schedule: StationSchedule): boolean {
    const sid = schedule.stationid
    if (!sid || !schedule.scheduledtime) return false
    const iso = String(schedule.scheduledtime).slice(0, 10)
    return this.lockoutDatesByStation.get(sid)?.has(iso) ?? false
  }

  isPreemptiblePromo(schedule: StationSchedule): boolean {
    return !!schedule.is_auto_fill
  }

  isDelayedAir(schedule: StationSchedule): boolean {
    return isAiredDelayed(schedule)
  }

  scheduleCustomer(schedule: StationSchedule): OrderCustomerDisplay | null {
    return getCustomerDisplay(schedule)
  }

  /** Amount charged for the spot (revenue_value), not its CPM value. */
  spotCost(schedule: StationSchedule): number {
    return scheduleSpotCost(schedule)
  }

  scheduleAudioUrl(schedule: StationSchedule): string | null {
    return schedule.audio_url ?? null
  }

  private cacheLockoutsForSchedules(schedules: StationSchedule[]): void {
    const stationIds = [...new Set(schedules.map((s) => s.stationid).filter((id): id is number => !!id))]
    for (const sid of stationIds) {
      if (this.lockoutDatesByStation.has(sid)) continue
      this.lockoutSvc.getLockedDates(sid).subscribe({
        next: (res) => this.lockoutDatesByStation.set(sid, new Set(res.lockedDates || [])),
        error: () => this.lockoutDatesByStation.set(sid, new Set()),
      })
    }
  }
}
